'use client';

import { useRef } from 'react';
import Link from 'next/link';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import Icon from '@/components/ui/AppIcon';
import Scene from './Scene';
import MobileHero from './MobileHero';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const HeroSection = () => {
    const containerRef = useRef<HTMLElement>(null);
    const badgeRef = useRef<HTMLDivElement>(null);
    const headlineRef = useRef<HTMLHeadingElement>(null);
    const subheadRef = useRef<HTMLParagraphElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const trustRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

        // Initial Entry Animation
        // proper cleanup of initial state to avoid race conditions
        tl.fromTo([badgeRef.current, headlineRef.current, subheadRef.current, ctaRef.current, trustRef.current],
            { y: 60, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 0.8,
                stagger: 0.1, // automated stagger instead of manual chaining for cleaner code
                ease: 'power3.out'
            }
        );

        // Parallax Scroll Effect
        const scrollTl = gsap.timeline({
            scrollTrigger: {
                trigger: containerRef.current,
                start: "top top",
                end: "bottom top",
                scrub: true, // simplified scrub
            }
        });

        // Use fromTo with immediateRender: false to avoid stomping the intro animation
        // This ensures scrub logic knows it starts at opacity: 1, regardless of current intro state
        scrollTl
            .fromTo(headlineRef.current, { y: 0, opacity: 1 }, { y: -120, opacity: 0, immediateRender: false }, 0)
            .fromTo(subheadRef.current, { y: 0, opacity: 1 }, { y: -80, opacity: 0, immediateRender: false }, 0)
            .fromTo(badgeRef.current, { y: 0, opacity: 1, scale: 1 }, { y: -150, opacity: 0, scale: 0.8, immediateRender: false }, 0)
            .fromTo(ctaRef.current, { y: 0, opacity: 1 }, { y: -40, opacity: 0, immediateRender: false }, 0.1)
            .fromTo(trustRef.current, { y: 0, opacity: 1 }, { y: -30, opacity: 0, immediateRender: false }, 0.1);

    }, { scope: containerRef });

    return (
        <>
            {/* Desktop View (Hidden on Mobile) */}
            <div className="hidden lg:block">
                <section ref={containerRef} className="relative pt-32 pb-40 overflow-hidden min-h-screen flex items-center justify-center bg-slate-50">
                    {/* 3D Background */}
                    <Scene />

                    {/* Overlay Gradient for readability - More transparent to show particles */}
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-50/20 via-slate-50/40 to-slate-50/80 z-0" />

                    {/* Floating Background Elements*/}
                    <div className="absolute top-20 right-10 w-72 h-72 bg-indigo-200/20 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-40 left-10 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
                        {/* Brand Badge */}
                        <div ref={badgeRef} className="inline-flex items-center space-x-3 px-5 py-3 bg-white/90 backdrop-blur-md rounded-full border-2 border-indigo-200 shadow-xl mb-8">
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                                <Icon name="CpuChipIcon" className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-700 via-indigo-600 to-blue-600 tracking-tight">SitemapGenius</span>
                            <span className="text-slate-300">|</span>
                            <span className="text-sm font-semibold text-indigo-600 uppercase tracking-wide">Digital Architecture</span>
                        </div>

                        {/* Main Headline */}
                        <h1 ref={headlineRef} className="text-5xl sm:text-6xl lg:text-8xl font-black text-slate-900 mb-8 tracking-tight leading-[1.1]">
                            We Don't Just <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500">Build</span>;
                            <br />
                            We <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">Elevate</span>
                        </h1>

                        {/* Subheading */}
                        <p ref={subheadRef} className="max-w-3xl mx-auto text-lg sm:text-xl text-slate-600 font-medium mb-12 leading-relaxed">
                            Complete digital ecosystems combining <span className="font-bold text-slate-900">full-stack development mastery</span> with <span className="font-bold text-slate-900">insider SEO strategies</span>. Transform your brand visibility with measurable results.
                        </p>

                        {/* CTA Buttons */}
                        <div ref={ctaRef} className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-12">
                            <Link
                                href="/contact"
                                className="group relative inline-flex items-center px-8 py-5 text-lg font-bold text-white bg-slate-900 rounded-full overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                            >
                                <span className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                <span className="relative flex items-center">
                                    Start Your Project
                                    <Icon name="ArrowRightIcon" className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </span>
                            </Link>
                        </div>

                        {/* Trust Signals */}
                        <div ref={trustRef} className="flex flex-col sm:flex-row items-center justify-center gap-8 pt-8 border-t border-slate-200">
                            <div className="flex items-center gap-2">
                                <div className="flex">
                                    {[...Array(5)].map((_, i) => (
                                        <Icon key={i} name="StarIcon" className="w-5 h-5 text-yellow-400 fill-current" />
                                    ))}
                                </div>
                                <span className="text-slate-700 font-semibold">4.9/5 Rating</span>
                            </div>
                            <div className="hidden sm:block w-px h-6 bg-slate-300" />
                            <div className="flex items-center gap-2">
                                <Icon name="CheckBadgeIcon" className="w-6 h-6 text-indigo-600" />
                                <span className="text-slate-700 font-semibold">Trusted by 25+ businesses</span>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Mobile View (App Design) */}
            <div className="block lg:hidden">
                <MobileHero />
            </div>
        </>
    );
};

export default HeroSection;
